// A single-series bar chart in inline SVG (dataviz skill): thin marks with a rounded top anchored to the
// baseline, a 2px gap between bars, recessive grid, one series so no legend (the title names it), hover
// tooltip per bar with a hit target wider than the mark, and a table view for accessibility.
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Bucket } from '../../types/api';

const W = 480;
const H = 160;
const PAD = { top: 14, right: 8, bottom: 26, left: 40 };

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v / p;
  const step = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return step * p;
}

export function BarChart({ title, buckets, format, color = '#107C41', periodLabel }: {
  title: string;
  buckets: Bucket[];
  format: (v: number) => string;
  color?: string;
  /** Short axis label of a bucket (`2026-09-01` → `9 Sep`). */
  periodLabel?: (period: string) => string;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const id = useId();
  const max = niceMax(Math.max(0, ...buckets.map((b) => b.value)));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const n = Math.max(1, buckets.length);
  const slot = plotW / n;
  const barW = Math.max(4, Math.min(28, slot - 2));
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const ticks = [0, 0.5, 1].map((f) => f * max);
  const label = periodLabel ?? ((p: string) => p);

  return (
    <figure className="rounded-md border border-line-card bg-white p-4" data-testid={`chart-${title}`}>
      <figcaption className="mb-2 flex items-center justify-between">
        <span className="text-[13px] font-medium text-ink">{title}</span>
        <button type="button" onClick={() => setTable((v) => !v)} className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint hover:text-ink">
          {table ? t('chart.showChart') : t('chart.showTable')}
        </button>
      </figcaption>
      {table ? (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">
              <th className="py-1">{t('chart.period')}</th>
              <th className="py-1 text-right">{t('chart.value')}</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b) => (
              <tr key={b.period} className="border-t border-line-row">
                <td className="py-1 font-mono text-[11.5px]">{b.period}</td>
                <td className="py-1 text-right">{format(b.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-labelledby={`${id}-title`}>
            <title id={`${id}-title`}>{title}</title>
            {ticks.map((tick) => (
              <g key={tick}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} stroke="#ebebe8" strokeWidth={1} />
                <text x={PAD.left - 6} y={y(tick) + 3.5} textAnchor="end" fontSize={10} fill="#8a8f94" fontFamily="JetBrains Mono, monospace">
                  {format(tick)}
                </text>
              </g>
            ))}
            {buckets.map((b, i) => {
              const x = PAD.left + i * slot + (slot - barW) / 2;
              const top = y(b.value);
              const h = Math.max(0, PAD.top + plotH - top);
              const r = Math.min(4, barW / 2, h);
              const active = hover === i;
              return (
                <g key={b.period} data-testid="bar" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                  <rect x={PAD.left + i * slot} y={PAD.top} width={slot} height={plotH} fill="transparent" />
                  {h > 0 && (
                    <path d={`M${x},${PAD.top + plotH} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${barW - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`} fill={color} opacity={hover === null || active ? 1 : 0.55} />
                  )}
                  {(n <= 12 || i % 2 === 0) && (
                    <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize={9.5} fill="#8a8f94" fontFamily="JetBrains Mono, monospace">
                      {label(b.period)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {hover !== null && buckets[hover] && (
            <div className="pointer-events-none absolute left-1/2 top-1 -translate-x-1/2 rounded-md border border-line-card bg-white px-2 py-1 font-mono text-[11px] text-ink shadow-sm" role="tooltip">
              {buckets[hover].period} · {format(buckets[hover].value)}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}
